import { onAuthStateChanged } from '@firebase/auth';
import type { User } from '@firebase/auth';
import { get, ref } from '@firebase/database';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { auth, database } from '@skippay/core/firebase/firebase';
import {
	BreadcrumbCategory,
	captureBreadcrumb,
	captureException,
} from '@skippay/core/global/errorTracking';
import {
	requestFirebaseLogin,
	setBanners,
	setConfig,
	setModals,
	setRequests,
} from '@skippay/core/modules/user';
import type { UserState } from '@skippay/core/modules/user/reducer';

type UserData = Pick<UserState, 'banners' | 'modals' | 'requests' | 'config'>;

interface Props {
	isEnabled: boolean;
}

export function useFirebaseSync({ isEnabled }: Props): void {
	const dispatch = useDispatch();

	useEffect(() => {
		if (!isEnabled) {
			return undefined;
		}

		const syncUserData = (user: User) => {
			captureBreadcrumb(
				BreadcrumbCategory.firebaseDatabase,
				'Downloading latest data from Firebase'
			);

			get(ref(database, `users/${user.uid}`))
				.then((snapshot) => {
					const data: UserData = snapshot.val() ?? {};

					dispatch(setBanners(data.banners ?? {}));
					dispatch(setModals(data.modals ?? {}));
					dispatch(setRequests(data.requests ?? {}));
					dispatch(setConfig(data.config ?? {}));
				})
				.catch((error) => {
					captureException(new Error('Error while downloading latest data from Firebase'), {
						extra: { error },
					});
				});
		};

		let isInitialState = true;

		return onAuthStateChanged(auth, (user) => {
			if (user) {
				syncUserData(user);
			} else if (isInitialState) {
				dispatch(requestFirebaseLogin());
			}

			isInitialState = false;
		});
	}, [dispatch, isEnabled]);
}
